<template>
    <div class="content mk-aff-comm">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_commissions") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- filters -->
            <card class="mb-3">
                <div class="row">
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.range") }}</label>
                        <el-date-picker v-model="f.range" type="daterange" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" :clearable="false" unlink-panels data-testid="aff-comm-range"></el-date-picker>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.status") }}</label>
                        <el-select v-model="f.status" class="w-100" clearable :placeholder="$t('marketing.affiliate.all')">
                            <el-option v-for="s in statuses" :key="s" :value="s" :label="tk('marketing.affiliate.commission.status_', s)"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.kind") }}</label>
                        <el-select v-model="f.kind" class="w-100" clearable :placeholder="$t('marketing.affiliate.all')">
                            <el-option value="order" :label="$t('marketing.affiliate.commission.kind_order')"></el-option>
                            <el-option value="adjustment" :label="$t('marketing.affiliate.commission.kind_adjustment')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-2 col-md-4 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.zero_reason") }}</label>
                        <el-select v-model="f.zero_reason" class="w-100" clearable :placeholder="$t('marketing.affiliate.all')" data-testid="aff-comm-zero">
                            <el-option value="paid_ad" :label="$t('marketing.affiliate.commission.zero_paid_ad')"></el-option>
                            <el-option value="self_purchase" :label="$t('marketing.affiliate.commission.zero_self_purchase')"></el-option>
                            <el-option value="rule_zero" :label="$t('marketing.affiliate.commission.zero_rule_zero')"></el-option>
                            <el-option value="no_rule" :label="$t('marketing.affiliate.commission.zero_no_rule')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-4 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.member.code") }}</label>
                        <el-input v-model="f.code" clearable placeholder="K7QH3X9M" data-testid="aff-comm-code"></el-input>
                    </div>
                    <div class="col-xl-3 col-md-4 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.order_no") }}</label>
                        <el-input v-model="f.order_no" clearable></el-input>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.payout") }}</label>
                        <el-input v-model="f.payout_id" clearable placeholder="ID"></el-input>
                    </div>
                    <div class="col-xl-7 col-md-9 form-group d-flex align-items-end flex-wrap" style="gap: 8px;">
                        <base-button type="primary" size="sm" :loading="loading" data-testid="aff-comm-search" @click="reload(1)"><i class="fa fa-search"></i> {{ $t("marketing.affiliate.search") }}</base-button>
                        <base-button type="secondary" size="sm" :loading="exporting" :disabled="schemaMissing" data-testid="aff-comm-export" @click="exportCsv"><i class="fa fa-file-csv"></i> {{ $t("marketing.affiliate.export_csv") }}</base-button>
                        <base-button v-if="canWriteFin && !schemaMissing" type="success" size="sm" data-testid="aff-adjust-open" @click="adjustOpen = true"><i class="fa fa-plus"></i> {{ $t("marketing.affiliate.commission.adjust") }}</base-button>
                    </div>
                </div>
                <small v-if="rangeError" class="text-danger">{{ $t("marketing.affiliate.err.invalid_range") }}</small>
            </card>

            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>

            <!-- sums by status -->
            <div class="row">
                <div v-for="s in statuses" :key="s" class="col-xl col-md-4 col-6">
                    <card class="mb-3" body-classes="py-3">
                        <div class="text-muted small">{{ tk("marketing.affiliate.commission.status_", s) }}</div>
                        <div class="h3 mb-0" :data-sum="s">฿{{ money(sums[s]) }}</div>
                    </card>
                </div>
            </div>

            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <el-table v-loading="loading" :data="rows" class="aff-table bon-table bon-tight" row-key="id" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-comm-table">
                    <!-- 1440 px + open sidebar (verifier B): fixed short columns; order
                         status under the order no, qty under the product, payout round
                         under the confirm date. -->
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.date')" width="92">
                        <template v-slot="{ row }">
                            <div>{{ fmt(row.created_at || row.placed_at).slice(0, 10) }}</div>
                            <div class="small text-muted">{{ fmt(row.created_at || row.placed_at).slice(11) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" label="Affiliate" min-width="110">
                        <template v-slot="{ row }">
                            <div class="aff-mono font-weight-bold">{{ row.affiliate_code }}</div>
                            <div class="small text-muted aff-ellipsis">{{ row.affiliate_name }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.order')" min-width="130">
                        <template v-slot="{ row }">
                            <router-link v-if="row.order_no" :to="{ path: '/backoffice/orders-overview', query: { q: row.order_no } }">{{ row.order_no }}</router-link>
                            <span v-else>-</span>
                            <div v-if="row.order_status" class="small text-muted">{{ $t("marketing.affiliate.commission.order_status") }} {{ row.order_status }}</div>
                            <div v-if="row.kind === 'adjustment'"><el-tag size="mini" type="warning">{{ $t("marketing.affiliate.commission.kind_adjustment") }}</el-tag></div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.product')" min-width="120">
                        <template v-slot="{ row }">
                            <span v-if="row.kind === 'adjustment'" class="small text-muted">{{ row.note }}</span>
                            <template v-else>
                                <div class="aff-ellipsis" :title="row.product_name">{{ row.product_name }}</div>
                                <div class="small text-muted">
                                    {{ $t("marketing.affiliate.commission.qty") }} {{ row.quantity }}<template v-if="row.target_type === 'promotion'"> · {{ $t("marketing.affiliate.rule.scope_promotion") }}</template>
                                </div>
                            </template>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.base')" width="116" align="right">
                        <template v-slot="{ row }">{{ row.kind === "order" ? money(row.base_amount) : "-" }}</template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.rate')" width="76" align="right">
                        <template v-slot="{ row }">{{ row.kind === "order" ? rate(row) : "-" }}</template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.amount')" width="108" align="right">
                        <template v-slot="{ row }"><span :class="{ 'text-danger': row.amount < 0 }" class="font-weight-bold">{{ money(row.amount) }}</span></template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.status')" width="124">
                        <template v-slot="{ row }">
                            <el-tag size="small" :type="statusTag(row.status)">{{ tk("marketing.affiliate.commission.status_", row.status) }}</el-tag>
                            <div v-if="row.zero_reason === 'paid_ad'">
                                <el-tooltip placement="top" :content="touch(row.paid_touch) || '-'">
                                    <el-tag size="mini" type="danger" class="mt-1" data-testid="aff-paid-ad">{{ $t("marketing.affiliate.commission.zero_paid_ad") }}</el-tag>
                                </el-tooltip>
                            </div>
                            <div v-else-if="row.zero_reason"><el-tag size="mini" type="info" class="mt-1">{{ tk("marketing.affiliate.commission.zero_", row.zero_reason) }}</el-tag></div>
                            <div v-if="row.status_reason" class="small text-muted">{{ reasonText(row.status_reason) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.mature_at') + ' / ' + $t('marketing.affiliate.commission.payout')" width="100">
                        <template v-slot="{ row }">
                            <div>{{ row.mature_at ? fmt(row.mature_at).slice(0, 10) : "-" }}</div>
                            <div v-if="row.payout_code" class="small text-muted">{{ row.payout_code }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" v-if="canWriteMk" :label="$t('marketing.affiliate.actions')" width="76" align="center" fixed="right">
                        <template v-slot="{ row }">
                            <el-dropdown v-if="cancellable(row)" trigger="click" @command="() => cancel(row)">
                                <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.affiliate.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item command="cancel" class="text-danger">{{ $t("marketing.affiliate.commission.cancel") }}</el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>
                <div slot="footer" class="d-flex justify-content-end px-3">
                    <el-pagination layout="total, prev, pager, next" :total="total" :page-size="limit" :current-page="f.page" @current-change="reload"></el-pagination>
                </div>
            </card>

            <p class="small text-muted" data-testid="aff-partial-return-note">{{ $t("marketing.affiliate.commission.partial_return_note") }}</p>
        </div>

        <affiliate-adjust-dialog :visible.sync="adjustOpen" @saved="reload()"></affiliate-adjust-dialog>
    </div>
</template>

<script>
import { DatePicker, Select, Option, Input, Table, TableColumn, Tag, Tooltip, Alert, Pagination, Dropdown, DropdownMenu, DropdownItem, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { isSchemaMissing } from "@/utils/marketing";
import { COMMISSION_STATUSES, LEDGER_LINK_KEYS, LIST_LIMIT, ledgerLinkFilters, canCancelCommission, commissionQuery, commissionStatusTag, defaultRange, paidTouchText, rangeOk, rateLabel, saveBlob } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";
import AffiliateAdjustDialog from "./components/AffiliateAdjustDialog.vue";

// การตลาด → Affiliate: รายการค่าคอม (Phase 3 spec 2026-10-13 §5.4).
export default {
    name: "MarketingAffiliateCommissions",
    directives: { loading: Loading.directive },
    components: {
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Alert.name]: Alert,
        [Pagination.name]: Pagination,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        AffiliateAdjustDialog
    },
    mixins: [affiliateMixin],
    data() {
        return {
            loading: false,
            exporting: false,
            schemaMissing: false,
            rangeError: false,
            rows: [],
            total: 0,
            sums: {},
            limit: LIST_LIMIT,
            statuses: COMMISSION_STATUSES,
            f: { page: 1, range: defaultRange(30), status: "", kind: "", zero_reason: "", code: "", order_no: "", payout_id: "" },
            adjustOpen: false,
            loadedOnce: false
        };
    },
    computed: {
        ...mapGetters({ canWriteMk: "Auth/canWriteMarketing", canWriteFin: "Auth/canWriteAffiliateFinance" })
    },
    created() {
        this.applyQuery();
        this.reload(1);
    },
    // keep-alive (taskbar): the member drawer / payout detail link here with
    // ?code= / ?payout_id= — re-read them when the cached tab is re-activated.
    activated() {
        if (!this.loadedOnce) return;
        if (this.applyQuery()) this.reload(1);
        else this.reload();
    },
    methods: {
        // ?code= / ?payout_id= deep link → filters (both fields, see
        // ledgerLinkFilters); true when the filters changed.
        applyQuery() {
            const link = ledgerLinkFilters(this.$route.query);
            if (!link) return false;
            const changed = LEDGER_LINK_KEYS.some((k) => String(this.f[k] || "") !== link[k]);
            if (changed) this.f = { ...this.f, ...link, page: 1 };
            return changed;
        },
        statusTag: commissionStatusTag,
        cancellable: canCancelCommission,
        rate(row) {
            return rateLabel(row.rate_type, row.rate, this.$t("marketing.affiliate.rule.per_item"));
        },
        touch(t) {
            return paidTouchText(t, this.fmt);
        },
        reasonText(r) {
            return this.$te("marketing.affiliate.commission.reason_" + r) ? this.tk("marketing.affiliate.commission.reason_", r) : r;
        },
        async reload(page) {
            if (page) this.f.page = page;
            this.rangeError = !rangeOk(this.f.range, 366);
            if (this.rangeError) return;
            this.loading = true;
            try {
                const res = await AffiliateService.commissions(commissionQuery(this.f, this.limit));
                const body = res.data || {};
                this.rows = body.data || [];
                this.total = body.total || 0;
                this.sums = (body.sums && body.sums.amount_by_status) || {};
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                this.total = 0;
                this.sums = {};
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
                this.loadedOnce = true;
            }
        },
        async exportCsv() {
            if (!rangeOk(this.f.range, 366)) return;
            this.exporting = true;
            try {
                const params = commissionQuery(this.f, this.limit);
                delete params.page;
                delete params.limit;
                saveBlob(await AffiliateService.exportCommissionsCsv(params), "affiliate-commissions.csv");
            } catch (err) {
                this.showError(err);
            } finally {
                this.exporting = false;
            }
        },
        async cancel(row) {
            const reason = await this.askReason(this.$t("marketing.affiliate.commission.cancel"), {
                text: `${row.affiliate_code} · ${row.order_no} · ฿${this.money(row.amount)}`,
                label: this.$t("marketing.affiliate.commission.cancel_reason"),
                danger: true
            });
            if (reason === null) return;
            try {
                await AffiliateService.cancelCommission(row.id, { reason });
                this.notifyOk();
                this.reload();
            } catch (err) {
                this.showError(err);
            }
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.aff-mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.04em;
}
</style>
